import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/${projectId}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setProject(d))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [companyId, projectId, API]);

  useEffect(() => { load(); }, [load]);

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!project) return <div style={pageStyle}><p>Project not found.</p></div>;

  const healthColor = { on_track: '#22c55e', at_risk: '#f59e0b', delayed: '#ef4444', critical: '#dc2626' };
  const statusColor = { draft: '#94a3b8', planned: '#3b82f6', active: '#10b981', on_hold: '#f59e0b', completed: '#8b5cf6', cancelled: '#ef4444', archived: '#6b7280' };

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'tasks', label: `Tasks (${project.open_tasks_count || 0})`, path: `/company/projects/${projectId}/tasks` },
    { key: 'milestones', label: `Milestones (${project.total_milestones_count || 0})`, path: `/company/projects/${projectId}/milestones` },
    { key: 'team', label: `Team (${project.team_count || 0})`, path: `/company/projects/${projectId}/team` },
    { key: 'deliverables', label: `Deliverables (${project.total_deliverables_count || 0})`, path: `/company/projects/${projectId}/deliverables` },
    { key: 'time', label: 'Time & Progress', path: `/company/projects/${projectId}/time` },
    { key: 'financial', label: 'Financial', path: `/company/projects/${projectId}/financial` },
  ];

  const infoRows = [
    ['Project Code', project.project_code],
    ['Type', project.project_type?.name || '-'],
    ['Client', project.client_name || '-'],
    ['Manager', project.project_manager?.name || '-'],
    ['Start Date', project.planned_start_date || '-'],
    ['Target End', project.target_end_date || '-'],
    ['Actual Start', project.actual_start_date || '-'],
    ['Completion', project.actual_completion_date || '-'],
    ['Progress Method', (project.progress_method || '').replace(/_/g, ' ')],
    ['Source', project.source_module || 'manual'],
  ];

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <button onClick={() => navigate('/company/projects/directory')} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginBottom: '8px', fontSize: '0.85rem' }}>← Back to Directory</button>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.4rem', fontWeight: 700 }}>{project.name}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ ...badgeStyle, backgroundColor: statusColor[project.status] || '#6b7280' }}>{(project.status || '').replace(/_/g, ' ')}</span>
            <span style={{ ...badgeStyle, backgroundColor: healthColor[project.health] || '#6b7280' }}>{(project.health || '').replace(/_/g, ' ')}</span>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{project.project_code}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>{project.progress_percentage || 0}%</div>
            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Progress</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px', overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => tab.path ? navigate(tab.path) : setActiveTab(tab.key)}
            style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#3b82f6' : '#64748b',
              borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-2px', whiteSpace: 'nowrap',
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Info */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>Project Information</h3>
            {infoRows.map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{label}</span>
                <span style={{ fontSize: '0.82rem', fontWeight: 500 }}>{val}</span>
              </div>
            ))}
            {project.description && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>Description</div>
                <p style={{ fontSize: '0.85rem', margin: 0, color: '#334155' }}>{project.description}</p>
              </div>
            )}
          </div>

          {/* Summary Cards */}
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Tasks</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{project.total_tasks_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Open Tasks</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>{project.open_tasks_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Overdue Tasks</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#ef4444' }}>{project.overdue_tasks_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Completed</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>{project.completed_tasks_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Milestones</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#8b5cf6' }}>{project.completed_milestones_count || 0}/{project.total_milestones_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Pending Deliverables</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#06b6d4' }}>{project.pending_deliverables_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Team Members</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{project.team_count || 0}</div></div>
              <div style={statCard}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Overdue MS</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#dc2626' }}>{project.overdue_milestones_count || 0}</div></div>
            </div>

            {/* Next Milestone */}
            {project.next_milestone && (
              <div style={{ ...cardStyle, marginTop: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Next Milestone</h4>
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{project.next_milestone.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Due: {project.next_milestone.due_date || 'Not set'}</div>
              </div>
            )}

            {/* Sub-projects */}
            {Array.isArray(project.sub_projects) && project.sub_projects.length > 0 && (
              <div style={{ ...cardStyle, marginTop: '12px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Sub-Projects</h4>
                {project.sub_projects.map(sp => (
                  <div key={sp.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => navigate(`/company/projects/${sp.id}`)}>
                    <span style={{ fontSize: '0.82rem', color: '#3b82f6' }}>{sp.name}</span>
                    <span style={{ ...badgeStyle, backgroundColor: statusColor[sp.status] || '#6b7280', fontSize: '0.65rem' }}>{sp.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statCard = { backgroundColor: '#fff', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const badgeStyle = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
